import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  useBreakpointValue,
  Badge,
  Spinner,
  Switch,
} from "@chakra-ui/react";
import { MdEdit, MdAdd } from "react-icons/md";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import CreateLanguage from "./CreateLanguage";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT, GOLD_LIGHT } from "constants/brandColors";
import { toLocalTime } from "utils/time";

const LANGUAGES_PAGE_ID = PAGE_IDS.LANGUAGES;

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

const ListLanguage = () => {
  const { t } = useTranslation();
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editableLanguage, setEditableLanguage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const toast = useToast();
  const modalSize = useBreakpointValue({ base: "full", md: "md" });
  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const tableHeaderColor = useColorModeValue("gray.600", "gray.200");
  const rowHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");

  const [permissions, setPermissions] = useState({ canList: true, canEdit: true, canInsert: true });

  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) return;
      const permission = role.permissions.find((p) => p.page_id === LANGUAGES_PAGE_ID);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canInsert: permission.insert_access || false,
        });
      }
    } catch (error) {
      // ignore malformed permissions blob
    }
  }, []);

  const fetchLanguages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/language?page_id=${LANGUAGES_PAGE_ID}`);
      setLanguages(response?.data || []);
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to fetch languages."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  const handleCreated = () => {
    setShowCreateModal(false);
    fetchLanguages();
  };

  const handleEdit = (language) => {
    setEditableLanguage({ ...language });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const data = {
        page_id: LANGUAGES_PAGE_ID,
        languageId: editableLanguage.id,
        code: editableLanguage.code.trim(),
        name: editableLanguage.name.trim(),
        isActive: editableLanguage.isActive,
      };
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/language`,
        "PUT",
        data
      );
      if (!response) {
        throw new Error("Failed to update language");
      }
      await fetchLanguages();
      setEditableLanguage(null);
      toast({
        title: t("Language updated successfully."),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to update language."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // A quick on/off toggle right in the table, separate from the full edit
  // modal — this is the "deactivate instead of delete" mechanism the backend
  // relies on, so it should be a one-click action, not buried in a form.
  const handleToggleActive = async (language) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/language`,
        "PUT",
        { page_id: LANGUAGES_PAGE_ID, languageId: language.id, isActive: !language.isActive }
      );
      if (!response) {
        throw new Error("Failed to update language");
      }
      setLanguages((prev) => prev.map((l) => (l.id === language.id ? { ...l, isActive: !l.isActive } : l)));
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to toggle language."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const filteredLanguages = languages.filter((l) => {
    const q = searchQuery.toLowerCase();
    return l.code.toLowerCase().includes(q) || l.name.toLowerCase().includes(q);
  });

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }} boxShadow="lg">
      <Flex px={{ base: "10px", md: "25px" }} justify="space-between" mb="8px" align="center" flexWrap="wrap">
        <Flex align="center">
          <Text color="gray.800" fontSize={{ base: "18px", md: "22px" }} fontWeight="700" lineHeight="100%" mb={{ base: "10px", md: "0" }}>
            {t("Languages")}
          </Text>
          <Button
            onClick={() => setShowCreateModal(true)}
            isDisabled={!permissions.canInsert}
            mb={1}
            ml={4}
            bg={GOLD_BORDER}
            color="#1A1A1A"
            _hover={{ bg: GOLD_TEXT, color: "white" }}
            _active={{ bg: GOLD_TEXT, color: "white" }}
            leftIcon={<MdAdd />}
            borderRadius="full"
            boxShadow="sm"
          >
            {t("Add Language")}
          </Button>
        </Flex>
        <Input
          placeholder={t("Search language")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="sm"
          w={{ base: "100%", md: "200px" }}
          borderColor={GOLD_BORDER}
          focusBorderColor={GOLD_BORDER}
        />
      </Flex>
      <Text fontSize="xs" color="gray.500" px={{ base: "10px", md: "25px" }} mb="16px">
        {t("Active languages appear as tabs on every News article. Deactivating a language stops new content in it — existing translations are kept.")}
      </Text>

      <Box overflowX="auto" overflowY="auto" maxH="60vh" px="0px">
        {loading ? (
          <Flex justify="center" py={10}><Spinner size="xl" /></Flex>
        ) : (
          <Table variant="simple">
            <Thead>
              <Tr bg={tableHeaderBg}>
                <Th color={tableHeaderColor} fontWeight="600">{t("Code")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Name")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Status")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Created")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Actions")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredLanguages.map((language) => (
                <Tr key={language.id} borderBottom="1px solid" borderColor="gray.100" _hover={{ bg: rowHoverBg }}>
                  <Td fontFamily="mono">{language.code}</Td>
                  <Td>{language.name}</Td>
                  <Td>
                    <Flex align="center" gap={2}>
                      <Badge colorScheme={language.isActive ? "green" : "gray"} variant="solid" px={2} py={1} borderRadius="md" fontSize="0.85em">
                        {language.isActive ? t("Active") : t("Inactive")}
                      </Badge>
                      <Switch
                        size="sm"
                        isChecked={language.isActive}
                        isDisabled={!permissions.canEdit}
                        onChange={() => handleToggleActive(language)}
                        colorScheme="brand"
                      />
                    </Flex>
                  </Td>
                  <Td>{language.createdAt ? toLocalTime(language.createdAt) : "—"}</Td>
                  <Td>
                    <Button
                      variant="outline"
                      borderColor={GOLD_BORDER}
                      color={GOLD_TEXT}
                      _hover={{ bg: GOLD_LIGHT }}
                      size="sm"
                      isDisabled={!permissions.canEdit}
                      onClick={() => handleEdit(language)}
                      leftIcon={<MdEdit />}
                    >
                      {t("Edit")}
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {editableLanguage && (
        <Modal isOpen={true} onClose={() => setEditableLanguage(null)} size={modalSize}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader borderBottom="1px solid" borderColor="gray.100">
              <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
                {t("Edit Language")}
              </Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box>
                <Text fontSize="sm" color="gray.600" mb="2">{t("Code")}</Text>
                <Input
                  value={editableLanguage.code}
                  onChange={(e) => setEditableLanguage({ ...editableLanguage, code: e.target.value })}
                  {...fieldProps}
                />
              </Box>
              <Box mt="16px">
                <Text fontSize="sm" color="gray.600" mb="2">{t("Name")}</Text>
                <Input
                  value={editableLanguage.name}
                  onChange={(e) => setEditableLanguage({ ...editableLanguage, name: e.target.value })}
                  {...fieldProps}
                />
              </Box>
            </ModalBody>
            <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
              <Button variant="outline" borderColor="gray.200" color="gray.700" onClick={() => setEditableLanguage(null)}>
                {t("Cancel")}
              </Button>
              <Button
                bg={GOLD_BORDER}
                color="#1A1A1A"
                _hover={{ bg: GOLD_TEXT, color: "white" }}
                _active={{ bg: GOLD_TEXT, color: "white" }}
                onClick={handleSave}
                isLoading={isSaving}
              >
                {t("Save")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} size={modalSize}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.100">
            <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
              {t("Add Language")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CreateLanguage onCreated={handleCreated} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default ListLanguage;
